/**
 * congregationalHealthSignals.js
 *
 * Detects congregational-level health signals from text + district state context.
 * NOT analytics. NOT KPIs. NOT corporate health metrics.
 *
 * Purpose: help Kevia understand when the pastoral context is not about one person
 * but about a community-level dynamic that shapes how to respond.
 *
 * DISTINCT from districtStateEngine (which tracks activity COUNTS).
 * This detects QUALITATIVE SIGNALS from the pastor's language about the congregation.
 *
 * Signals:
 *   agotamiento_colectivo    — congregation-wide fatigue, too many activities
 *   desanimo_congregacional  — widespread discouragement, low engagement
 *   tension_liderazgo        — tension among leaders or between pastor and leaders
 *   fragmentacion_relacional — relational disconnection within the congregation
 *   restauracion_comunitaria — positive restoration or renewal signs
 *   aislamiento_pastoral     — the congregation feeling pastorally neglected
 *   presion_ministerial      — excessive ministry pressure on members
 *   ninguno                  — no congregational signal inferred
 */

const SIGNAL_PATTERNS = [
  {
    signal: "tension_liderazgo",
    patterns: [
      /(?:anciano[s]?|di[aá]cono[s]?|líder(?:es)?|junta)\s+(?:en\s+conflicto|no\s+(?:están?|están?s|se)|están?\s+dividid|tiene[n]?\s+problemas|no\s+(?:apoyan?|colabora[n]?))/i,
      /tensión\s+(?:con\s+(?:los\s+)?(?:ancianos?|diáconos?|líderes?|junta)|en\s+(?:el\s+)?liderazgo)/i,
      /conflicto\s+(?:con|en)\s+(?:el\s+)?liderazgo/i,
      /división\s+(?:en\s+)?(?:el\s+)?(?:liderazgo|entre\s+(?:los\s+)?líderes?)/i,
    ],
  },
  {
    signal: "agotamiento_colectivo",
    patterns: [
      /(?:la\s+gente|los\s+miembros?|la\s+congregaci[oó]n|todos)\s+(?:está[n]?\s+)?(?:agotados?|cansados?|sobrecargados?)/i,
      /demasiadas?\s+actividades?\s+en\s+la\s+iglesia/i,
      /(?:hay|tienen?)\s+demasiado\s+(?:que\s+hacer|actividades?|eventos?)/i,
      /sobrecarga\s+(?:de\s+actividades?|ministerial|congregacional)/i,
    ],
  },
  {
    signal: "desanimo_congregacional",
    patterns: [
      /(?:la\s+gente|los\s+miembros?|la\s+congregaci[oó]n)\s+(?:está[n]?\s+)?(?:desanimad[ao]s?|frustrad[ao]s?|sin\s+entusiasmo|apátic[ao]s?)/i,
      /poca\s+(?:asistencia|participaci[oó]n|respuesta)\s+de\s+la\s+(?:congregaci[oó]n|iglesia)/i,
      /(?:nadie|pocos)\s+(?:quiere[n]?|participa[n]?|viene[n]?|responde[n]?)\s+(?:a|en)/i,
    ],
  },
  {
    signal: "fragmentacion_relacional",
    patterns: [
      /(?:hay|existe)\s+(?:divisi[oó]n|fragmentaci[oó]n|separaci[oó]n)\s+(?:en|entre)\s+(?:la\s+)?(?:iglesia|congregaci[oó]n|los\s+miembros?)/i,
      /grupos?\s+(?:divididos?|separados?|que\s+no\s+se\s+(?:hablan?|llevan?))\s+en\s+(?:la\s+)?(?:iglesia|congregaci[oó]n)/i,
      /(?:la\s+iglesia|comunidad)\s+(?:está|se\s+siente)\s+dividida/i,
    ],
  },
  {
    signal: "restauracion_comunitaria",
    patterns: [
      /(?:restauraci[oó]n|renovaci[oó]n|avivamiento|reconciliaci[oó]n)\s+(?:en\s+)?(?:la\s+)?(?:iglesia|congregaci[oó]n|comunidad)/i,
      /(?:la\s+iglesia|congregaci[oó]n)\s+(?:está\s+)?(?:creciendo|mejorando|uniéndose)/i,
      /se\s+est[aá]\s+restaurando\s+la\s+(?:unidad|comunión|hermandad)/i,
    ],
  },
  {
    signal: "aislamiento_pastoral",
    patterns: [
      /(?:los\s+miembros?|la\s+gente)\s+(?:no\s+(?:sienten?|perciben?)\s+(?:apoyo|presencia)\s+pastoral)/i,
      /(?:no\s+los\s+(?:visito|visita|visitamos)|sin\s+visitas?\s+(?:pastorales?|frecuentes?))\s+(?:por\s+meses?|hace\s+tiempo)/i,
      /la\s+iglesia\s+(?:se\s+siente|está)\s+abandonada\s+pastoralmente/i,
    ],
  },
  {
    signal: "presion_ministerial",
    patterns: [
      /(?:mucha\s+)?presión\s+(?:sobre\s+)?(?:los\s+)?(?:voluntarios?|miembros?|líderes?)\s+para\s+(?:servir|participar)/i,
      /obligados?\s+a\s+(?:servir|participar|asistir)/i,
      /(?:se\s+les\s+)?exige[n]?\s+demasiado\s+a\s+(?:los\s+)?(?:miembros?|voluntarios?|líderes?)/i,
    ],
  },
];

/**
 * @param {{
 *   text:          string,
 *   emotion:       object|null,
 *   intent:        string,
 *   districtState: object|null,   — from getDistrictState()
 * }} params
 * @returns {{
 *   signal:      string,
 *   instruction: string|null,
 * }}
 */
export function detectCongregationalSignals({ text, emotion, intent, districtState }) {
  const t = text ?? "";

  for (const { signal, patterns } of SIGNAL_PATTERNS) {
    if (patterns.some((p) => p.test(t))) {
      return { signal, instruction: _instruction(signal) };
    }
  }

  // District state fallback: if ministry pressure is critical AND intent is analysis/planning
  // → surface collective exhaustion signal even without explicit text
  if (
    districtState?.priorityReport?.pressure === "critical" &&
    (intent === "planning" || intent === "analysis") &&
    emotion?.dominantEmotion === "exhaustion"
  ) {
    return { signal: "agotamiento_colectivo", instruction: _instruction("agotamiento_colectivo") };
  }

  return { signal: "ninguno", instruction: null };
}

const INSTRUCTIONS = {
  tension_liderazgo:
    "Hay tensión en el liderazgo. Responde con suma prudencia — las palabras sobre líderes tienen peso eclesial. No simplifiques.",
  agotamiento_colectivo:
    "La congregación muestra señales de agotamiento. Favorece el descanso comunitario sobre más actividad.",
  desanimo_congregacional:
    "Hay desánimo congregacional. Ancla en esperanza sin forzar entusiasmo artificial.",
  fragmentacion_relacional:
    "Hay fragmentación relacional en la comunidad. El cuidado de la unidad requiere paciencia y discernimiento.",
  restauracion_comunitaria:
    "Hay señales de restauración comunitaria. Afirma el proceso con gratitud y sin presión.",
  aislamiento_pastoral:
    "La comunidad puede sentir falta de presencia pastoral. Prioriza conexión antes que programa.",
  presion_ministerial:
    "Hay presión ministerial excesiva sobre los miembros. Favorece gracia y descanso, no más demandas.",
};

function _instruction(signal) {
  return INSTRUCTIONS[signal] ?? null;
}
